import { VerEventoComponent } from './../../private/admin/pages/agenda/agenda/components/ver-evento/ver-evento.component';
import { EditarEventoComponent } from './../../private/admin/pages/agenda/agenda/components/editar-evento/editar-evento.component';
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FullCalendarModule } from '@fullcalendar/angular';
import {
  CalendarOptions,
  EventClickArg,
  EventSourceInput,
} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

import { ModalService } from '../modal/modal.service';
import { NewEventComponent } from './new-event/new-event.component';
import { CriarEventoComponent } from '../../private/admin/pages/agenda/agenda/components/criar-evento/criar-evento.component';
import { AgendaService } from '../../private/admin/pages/agenda/agenda/agenda.service';
import { LoaderService } from '../loader/loader.service';
import { ToastService } from '../toast/toast.service';
import { UserService } from '../services/user/user.service';
import { User } from '../../models/authentication.interface';
import {
  permissaoBehavior,
  PermissaoEnum,
  RoleEnum,
} from '../../models/role.enum';

@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [FullCalendarModule],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss',
})
export class CalendarComponent implements OnInit, AfterViewInit {
  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    selectable: true,
    editable: true,
    weekends: true,
    nowIndicator: true,
    dateClick: (args) => this.dayClick(args),
    eventClick: (arg) => this.editarEvento(arg),
    locale: 'pt-br',
    buttonText: {
      today: 'Ir para Hoje',
    },
    expandRows: true,
    height: 'auto',
    contentHeight: 600,
    aspectRatio: 1.8,
    eventDisplay: 'block',
    eventTimeFormat: {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    },
    dayMaxEvents: 3,
    moreLinkClick: 'popover',
  };
  
  userLogado: permissaoBehavior | undefined;
  
  constructor(
    private readonly modal: ModalService,
    private readonly agendaService: AgendaService,
    private readonly loaderService: LoaderService,
    private readonly toastService: ToastService,
    private readonly userService: UserService,
  ) {}
  
  ngAfterViewInit(): void {
    setTimeout(() => {
      document.querySelectorAll('.fc-button').forEach((button) => {
        (button as HTMLElement).style.backgroundColor = '#39693B';
      });
      document.querySelectorAll('.fc-toolbar-chunk').forEach((chunk) => {
        (chunk as HTMLElement).style.display = 'flex';
      });
    });
  }

  ngOnInit(): void {
    this.listenEvents();
    this.userService.permissao.subscribe((value) => {
      if (value !== undefined) {
        this.userLogado = this.userService.permissaoRole;
      }
    });

    this.userService.escola.subscribe((value) => {
      if (value !== undefined) {
        this.listenEvents();
      }
    });

    this.agendaService.cadastroCompleto.subscribe(() => {
      this.listenEvents();
    });
  }

  listenEvents() {
    this.loaderService
      .executeWithLoader(this.agendaService.listarPorEscolas())
      .subscribe({
        next: (value) => {
          this.calendarOptions.events = value.data.map((item) => {
            const hasAttachments = item.arquivo && item.arquivo.length > 0;
            const timeInfo = item.diaInteiro 
              ? 'Dia inteiro' 
              : `${this.formatarHora(item.horaInicio)} - ${this.formatarHora(item.horaFim)}`;
            
            const title = `${item.assunto} | ${timeInfo}${hasAttachments ? ' 📎' : ''}`;
            
            return {
              title: title,
              date: item.data,
              backgroundColor: this.getEventColor(item.categoria),
              textColor: '#000',
              borderColor: this.getEventColor(item.categoria),
              id: item.documentId,
              classNames: ['meu-evento'],
              extendedProps: {
                categoria: item.categoria,
                enviarPara: item.enviarPara,
                hasAttachments: hasAttachments,
                diaInteiro: item.diaInteiro
              }
            };
          });
        },
        error: (error) => {
          console.error('Erro ao carregar eventos:', error);
          this.toastService.notify({
            message: 'Erro ao carregar eventos da agenda.',
            type: 'ERROR',
          });
        },
      });
  }

  getEventColor(categoria: string): string {
    const colors: { [key: string]: string } = {
      'Comunicados': '#BAF0B6',
      'Atividades Acadêmicas': '#FFE6B3',
      'Evento Especial': '#FFB3B3',
      'Atividades Extraescolares': '#B3D9FF',
      'Assuntos Administrativos': '#E6B3FF',
      'Reuniões': '#B3FFB3'
    };
    
    return colors[categoria] || '#BAF0B6';
  }

  formatarHora(hora: string): string {
    if (!hora) return '';
    return hora.slice(0, 2) + ':' + hora.slice(2);
  }

  dayClick(res: { dateStr: string }) {
    const dataSelecionada = new Date(res.dateStr + 'T00:00:00');
    const dataAtual = new Date();
    dataAtual.setHours(0, 0, 0, 0);

    if (this.userLogado?.id !== PermissaoEnum.ROLE_RESPONSAVEL) {
      if (dataSelecionada < dataAtual) {
        this.toastService.notify({
          message: 'Não é possível criar eventos para datas passadas.',
          type: 'WARNING',
        });
      } else {
        this.modal.openWithData(CriarEventoComponent, res);
      }
    } else {
      this.toastService.notify({
        message: 'Responsáveis não podem criar eventos na agenda.',
        type: 'WARNING',
      });
    }
  }

  editarEvento(arg: EventClickArg) {
    const eventData = arg.event;
    const extendedProps = eventData.extendedProps;
    
    // Verificar permissão para editar
    if (this.userLogado?.id === PermissaoEnum.ROLE_RESPONSAVEL) {
      this.toastService.notify({
        message: 'Responsáveis não podem editar eventos na agenda.',
        type: 'WARNING',
      });
      return;
    }
    
    // Mostrar informações do evento no console para debug
    console.log('Evento clicado:', {
      id: eventData.id,
      title: eventData.title,
      categoria: extendedProps['categoria'],
      enviarPara: extendedProps['enviarPara'],
      hasAttachments: extendedProps['hasAttachments'],
      diaInteiro: extendedProps['diaInteiro']
    });

    // Buscar dados completos da agenda para edição
    this.agendaService.buscar(eventData.id).subscribe({
      next: (response) => {
        // Abrir modal de edição com dados completos da agenda
        this.modal.openWithData(EditarEventoComponent, response.data);
      },
      error: (error) => {
        console.error('Erro ao buscar dados da agenda:', error);
        this.toastService.notify({
          message: 'Erro ao carregar dados da agenda para edição.',
          type: 'ERROR',
        });
      }
    });
  }
}
